import {InfiniteData, UseInfiniteQueryResult} from '@tanstack/react-query';

function flatten<T>(array: T[][]): T[] {
    return ([] as T[]).concat(...array);
}

/** Returns flattened array of the items of Infinite query */
export function extractInfiniteQueryItems<TPage, TItem = TPage>(
    query: UseInfiniteQueryResult<InfiniteData<TPage>>,
    mapper: (page: TPage) => TItem[]
): TItem[] | undefined {
    return query.data ? flatten(query.data.pages.map(mapper)) : undefined;
}

export function updateDocumentInCache(
    queryData: InfiniteData<PageResponse<EditModeDoc>>,
    updatedData: Omit<Partial<EditModeDoc>, 'status'> & {accession: string}
) {
    return {
        ...queryData,
        pages: queryData.pages.map((page) => ({
            ...page,
            data: page.data.map(
                (docInfo): EditModeDoc =>
                    docInfo.accession === updatedData.accession
                        ? ({...docInfo, ...updatedData} as typeof docInfo)
                        : docInfo
            )
        }))
    };
}

/** Adds document to PageResponses and remove all previous copies of this document */
export function addDocumentToCache(
    queryData: InfiniteData<PageResponse<EditModeDoc>>,
    document: EditModeDoc,
    addToTheEnd?: boolean
) {
    const pagesNumber = queryData.pages.length;

    const getPageWithAddedDoc = (pageData: EditModeDoc[]) => {
        const withPrevVersionsExcluded = pageData.filter((data) => data.id !== document.id);

        return addToTheEnd
            ? [...withPrevVersionsExcluded, document]
            : [document, ...withPrevVersionsExcluded];
    };

    return {
        ...queryData,
        pages: queryData.pages.map((page, index) => {
            const isLastPage = index === pagesNumber - 1;
            const isFirstPage = index === 0;
            const shouldAddDocToThisPage =
                (addToTheEnd && isLastPage) || (!addToTheEnd && isFirstPage);

            const pageData = shouldAddDocToThisPage ? getPageWithAddedDoc(page.data) : page.data;

            return {
                ...page,
                data: pageData,
                total: page.total + 1
            };
        })
    };
}

export function removeDocumentFromCache(
    queryData: InfiniteData<PageResponse<EditModeDoc>>,
    accession: string
) {
    return {
        ...queryData,
        pages: queryData.pages.map((page) => ({
            ...page,
            total: page.total > 0 ? page.total - 1 : page.total,
            data: page.data.filter((document) => document.accession !== accession)
        }))
    };
}
